import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type {
  CreateImportPayment,
  Payment,
  Subscription,
  SubscriptionCreateData,
} from '../features/subscription/entity';

import dayjs from 'dayjs';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import type { Invoice } from '@shared/entity';
import { QueryClient } from '@tanstack/react-query';
import {
  getSubscription,
  postCancelSubscription,
  postChangeSubscription,
  postCreateSubscription,
  postExtendSubscription,
} from '../features/subscription/api';
import type { CancellationFeedback } from '../features/subscription/api';
import { getLogs } from '../features/logs/api';

import { LogData } from '../features/logs/entity';
import {
  getPayment,
  postCreateImportPayment,
  getUser,
  updateUserData,
  uploadUserAvatar,
  deleteUser,
  getInvoices,
  UserOverview,
  getUsersOverview,
  markNewsSeen,
} from '../features/profile/api';
import { UserForm } from '@features/profile/entity';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
    },
  },
});

export const getSubscriptionQuery = (): {
  queryKey: string[];
  queryFn: () => Promise<Subscription>;
} => {
  return { queryKey: ['subscription'], queryFn: () => getSubscription() };
};

export const useSubscriptionQuery = (refetchInterval?: number) => {
  return useQuery<Subscription>({
    ...getSubscriptionQuery(),
    refetchOnMount: true,
    refetchInterval,
  });
};

export const useExtendSubscriptionMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { trackEvent } = useAnalytics();

  const mutation = useMutation<Subscription, Error, string>({
    mutationFn: async (subscriptionId: string) => postExtendSubscription(subscriptionId),
    onSuccess: subscription => {
      trackEvent('subscription_renewed', {
        plan_tier: subscription.plan,
        price_czk: subscription.price,
      });
      addSnackBarMessage({ text: 'Předplatné prodlouženo', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });

  return { mutation };
};

export const useSubscriptionMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { trackEvent } = useAnalytics();

  const mutation = useMutation<
    {
      code: number;
      message: string;
      transId: string;
      redirect: string;
    },
    { message: string; code: number },
    SubscriptionCreateData
  >({
    mutationFn: async data => postCreateSubscription(data),
    onSuccess: (data, variables) => {
      addSnackBarMessage({ text: 'Platba založena', type: 'success' });
      window.location.assign(data.redirect);
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });

  return { ...mutation };
};

export const useChangeSubscriptionMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { trackEvent } = useAnalytics();

  const mutation = useMutation<
    Awaited<ReturnType<typeof postChangeSubscription>>,
    { message: string; code: number },
    SubscriptionCreateData
  >({
    mutationFn: async data => postChangeSubscription(data),
    onSuccess: (data, variables) => {
      const currentSubscription = queryClient.getQueryData<Subscription>(['subscription']);
      if (currentSubscription) {
        trackEvent('subscription_changed', {
          from_tier: currentSubscription.plan,
          to_tier: variables.plan,
        });
      }
      if ('paymentRequired' in data && data.paymentRequired === false) {
        queryClient.invalidateQueries();
        addSnackBarMessage({
          text: 'Tarif byl změněn. Zkušební období pokračuje beze změny.',
          type: 'success',
        });
        return;
      }
      addSnackBarMessage({ text: 'Změna předplatného byla zahájena', type: 'success' });
      if ('redirect' in data) window.location.assign(data.redirect);
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });

  return { ...mutation };
};

export const useImportPaymentQuery = () => {
  return useQuery<Payment | null>({
    queryKey: ['importPayment'],
    queryFn: () => getPayment(),
  });
};

export const useImportPaymentMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();

  const mutation = useMutation<
    {
      code: number;
      message: string;
      transId: string;
      redirect: string;
    },
    { message: string; code: number },
    CreateImportPayment
  >({
    mutationFn: async data => postCreateImportPayment(data),
    onSuccess: data => {
      addSnackBarMessage({ text: 'Platba založena', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['importPayment'] });
      window.location.assign(data.redirect);
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });

  return { ...mutation };
};

export const useCancelSubscriptionMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { trackEvent } = useAnalytics();

  return useMutation<boolean, Error, { subscriptionId: string; feedback: CancellationFeedback }>({
    mutationFn: async ({ subscriptionId, feedback }) =>
      postCancelSubscription(subscriptionId, feedback),
    onSuccess: (_, variables) => {
      const subscription = queryClient.getQueryData<Subscription>(['subscription']);
      if (subscription) {
        trackEvent('subscription_cancelled', {
          plan_tier: subscription.plan,
          reason: variables.feedback.reason,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      addSnackBarMessage({
        text:
          subscription?.billingPeriod === 'YEARLY'
            ? 'Roční obnovení vypnuto. Předplatné zůstává platné do konce zaplaceného období.'
            : 'Předplatné ukončeno.',
        type: 'success',
      });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });
};

export const useUserDataQuery = () => {
  return useQuery<UserForm>({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: 20 * 60 * 1000,
    refetchOnMount: false,
  });
};
export const useUpdateUserMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { trackEvent, trackOnboardingStep } = useAnalytics();

  const mutation = useMutation<UserForm, Error, UserForm>({
    mutationFn: async data => updateUserData(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      if (variables.allowPublicReservations === true) {
        trackOnboardingStep('publicReservationSetup', 10);
      }
      if (variables.colorScheme) {
        trackEvent('color_scheme_changed', { color: variables.colorScheme });
      }
      if (variables.loyaltyPercent !== undefined) {
        trackEvent('loyalty_program_used', { action: 'configuration_changed' });
      }
      addSnackBarMessage({ text: 'Uživatel upraven', type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });

  return { mutation };
};

export const useUploadAvatarMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();

  return useMutation<UserForm, Error, string>({
    mutationFn: async imageData => uploadUserAvatar(imageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      addSnackBarMessage({ text: 'Profilová fotka nahrána', type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });
};

export const useMarkNewsSeenMutation = () => {
  return useMutation<UserForm, Error>({
    mutationFn: () => markNewsSeen(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useDeleteUserMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();

  return useMutation<{ deletionScheduledAt: string }, Error>({
    mutationFn: async () => deleteUser(),
    onSuccess: (data: { deletionScheduledAt: string }) => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      const formattedDate = dayjs(data.deletionScheduledAt).format('DD.MM.YYYY');
      addSnackBarMessage(
        { text: `Uživatel smazán (naplánováno na ${formattedDate})`, type: 'success' },
        10000,
      );
    },
    onError: error => {
      addSnackBarMessage({ text: error.message, type: 'error' });
    },
  });
};

export const useInvoicesQuery = () => {
  return useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => getInvoices(),
  });
};

export const useSuspenseLogsQuery = () => {
  return useSuspenseQuery<LogData[]>({
    queryKey: ['logs'],
    queryFn: () => {
      return getLogs();
    },
  });
};

export const useUsersOverviewQuery = () => {
  return useQuery<UserOverview[]>({
    queryKey: ['usersOverview'],
    queryFn: () => getUsersOverview(),
  });
};
